import { Button, ButtonGroup, cn, Input, Link, Progress, Tooltip } from '@heroui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, MessageSquare, Phone, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { formatPersonaConfig } from 'utils/funcs/send-call/parseForJson'
import PersonaManagement from './PersonaManagement'

export default function SendCallCTA({
  type = 'call',
  ...props
}) {
  const isInbound = type === 'inbound'
  const isBatch = type === 'batch'
  const isCall = type === 'call'
  const isSMS = type === 'sms'
  const color = 'primary'

  const [activePreset, setActivePreset] = useState(null)
  const [presetName, setPresetName] = useState(activePreset?.name)
  const { getValues } = useFormContext()
  const queryClient = useQueryClient()

  const buttonLabel = useMemo(() => {
    if (isInbound)
      return 'Save Inbound Call Changes'
    if (isSMS)
      return 'Save SMS Changes'
    if (isBatch)
      return 'Review Batch'
    return 'Send Call'
  }, [activePreset, isInbound, isSMS, isBatch])

  const buttonIcon = useMemo(() => {
    if (isCall)
      return <Send size={16} />
    if (isInbound)
      return <Phone size={16} />
    if (isSMS)
      return <MessageSquare size={16} />
    return undefined
  }, [isCall, isInbound, isSMS])

  const { mutate: handleSavePersona, isPending: isSubmitting } = useMutation({
    mutationFn: () => $fetch('/call_config', {
      method: 'PATCH',
      body: {
        id: activePreset?.id,
        name: presetName,
        call_config: formatPersonaConfig(getValues()),
      },
    }),
    onSuccess: () => {
      toast.success('Persona saved successfully')
    },
    onError: () => {
      toast.error('Failed to save persona')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['configs'] })
      setActivePreset(null)
    },
  })

  useEffect(() => {
    setPresetName(activePreset?.name)
  }, [activePreset])

  return (
    <div className={cn('flex fixed bottom-0 !pl-16 right-0 shrink-0 h-[54px] px-2 w-full gap-4 transition-colors duration-300 items-center justify-between border-t border-gray-200 z-20 bg-white', { '!border-none': activePreset })}>
      {activePreset
        ? (
            <>
              <Progress
                isIndeterminate
                aria-label="Editing Persona"
                color="primary"
                size="sm"
                classNames={{
                  base: 'absolute top-0 left-0 w-full h-full',
                  indicator: '!duration-[5000ms]',
                  track: '!h-[2px] bg-gray-200',
                }}
              />
              <Input
                radius="none"
                fullWidth
                startContent={<p className="text-nowrap text-xs text-gray-500">Editing Persona</p>}
                variant="faded"
                placeholder="Persona Name"
                value={presetName}
                onValueChange={setPresetName}
              />
              <Button
                variant="light"
                color="danger"
                radius="none"
                onPress={() => setActivePreset(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                radius="none"
                onPress={handleSavePersona}
                color="primary"
                variant="solid"
                disableRipple
                isLoading={isSubmitting}
                {...props}
              >
                Save Changes
              </Button>
            </>
          )
        : (
            <>
              <ButtonGroup className="preset-group">
                {!isCall && (
                  <Tooltip content={isInbound ? 'Back to Phone Numbers' : 'Back to Batches'}>
                    <Button
                      as={Link}
                      isIconOnly
                      color={color}
                      variant="light"
                      href={isInbound || isSMS ? '/dashboard/phone-numbers' : '/dashboard/batches'}
                      radius="none"
                    >
                      <ChevronLeft />
                    </Button>
                  </Tooltip>
                )}

                <PersonaManagement setActivePreset={setActivePreset} />

              </ButtonGroup>

              <div className="flex items-center gap-4">
                {props.children}

                <div className="relative inline-block" id="rainbow-button-wrapper">

                  <Button
                    type="submit"
                    radius="none"
                    color={color}
                    disableRipple
                    className="rainbow-button !h-9"
                    isLoading={isSubmitting}
                    {...props}
                  >
                    {buttonLabel}
                    {buttonIcon}
                  </Button>

                  <style jsx>
                    {`#rainbow-button-wrapper {
                    position: relative;
                  }
                  
                  #rainbow-button-wrapper::before,
                  #rainbow-button-wrapper::after,
                  #rainbow-button-wrapper .before-elem,
                  #rainbow-button-wrapper .after-elem {
                    content: '';
                    position: absolute;
                    inset: 0;
                    transition: all 0.3s ease-out;
                    opacity: 0;
                  }
                  
                  #rainbow-button-wrapper::before {
                    background-color: #f59e0b; /* Yellow */
                    z-index: 1;
                  }
                  
                  #rainbow-button-wrapper::after {
                    background-color: #f97316; /* Orange */
                    z-index: 2;
                    transition-delay: 0.075s;
                  }
                  
                  #rainbow-button-wrapper .rainbow-button {
                    position: relative;
                    z-index: 5;
                  }
                  
                  #rainbow-button-wrapper:hover::before {
                    opacity: 1;
                    transform: translate(-8px, 8px);
                  }
                  
                  #rainbow-button-wrapper:hover::after {
                    opacity: 1;
                    transform: translate(-6px, 6px);
                  }
                  
                  #rainbow-button-wrapper::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background-color: #3b82f6; /* Blue */
                    z-index: 3;
                    transition-delay: 0.15s;
                    opacity: 0;
                  }
                  
                  #rainbow-button-wrapper:hover::before {
                    opacity: 1;
                    transform: translate(-4px, 4px);
                  }
            `}
                  </style>
                </div>

              </div>
            </>
          )}
    </div>
  )
}
